import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, Shield, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../../api';

const CambiarPasswordModal = ({ isOpen, onClose, userEmail }) => {
  const [formData, setFormData] = useState({
    passwordActual: '',
    passwordNueva: '',
    passwordConfirmar: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    actual: false,
    nueva: false,
    confirmar: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleClose = () => {
    setFormData({
      passwordActual: '',
      passwordNueva: '',
      passwordConfirmar: ''
    });
    setErrors({});
    setShowPasswords({
      actual: false,
      nueva: false,
      confirmar: false
    });
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.passwordActual) {
      newErrors.passwordActual = 'La contraseña actual es requerida';
    }

    if (!formData.passwordNueva) {
      newErrors.passwordNueva = 'La nueva contraseña es requerida';
    } else if (formData.passwordNueva.length < 6) {
      newErrors.passwordNueva = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!formData.passwordConfirmar) {
      newErrors.passwordConfirmar = 'Confirma tu nueva contraseña';
    } else if (formData.passwordNueva !== formData.passwordConfirmar) {
      newErrors.passwordConfirmar = 'Las contraseñas no coinciden';
    }

    if (formData.passwordActual && formData.passwordNueva && formData.passwordActual === formData.passwordNueva) {
      newErrors.passwordNueva = 'La nueva contraseña debe ser diferente a la actual';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor corrige los errores en el formulario');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/cambiar-password`, {
        password_actual: formData.passwordActual,
        password_nueva: formData.passwordNueva
      });

      toast.success('✅ Contraseña actualizada correctamente', {
        description: 'Tu nueva contraseña ya está activa',
        duration: 4000
      });

      handleClose();
    } catch (error) {
      if (error.response?.status === 401) {
        setErrors({ passwordActual: 'La contraseña actual es incorrecta' });
        toast.error('Contraseña actual incorrecta');
      } else {
        toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
      }
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, text: '', color: '' };
    
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return { level: 1, text: 'Débil', color: 'text-red-600 bg-red-100' };
    if (strength <= 3) return { level: 2, text: 'Media', color: 'text-yellow-600 bg-yellow-100' };
    if (strength <= 4) return { level: 3, text: 'Fuerte', color: 'text-green-600 bg-green-100' };
    return { level: 4, text: 'Muy Fuerte', color: 'text-blue-600 bg-blue-100' };
  };

  const passwordStrength = getPasswordStrength(formData.passwordNueva);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="w-6 h-6 text-blue-600" />
            Cambiar Contraseña
          </DialogTitle>
          <DialogDescription>
            Actualiza tu contraseña de manera segura. Asegúrate de usar una contraseña fuerte.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Current Password */}
          <div>
            <Label htmlFor="passwordActual" className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-gray-600" />
              Contraseña Actual
            </Label>
            <div className="relative">
              <Input
                id="passwordActual"
                type={showPasswords.actual ? 'text' : 'password'}
                value={formData.passwordActual}
                onChange={(e) => {
                  setFormData({ ...formData, passwordActual: e.target.value });
                  setErrors({ ...errors, passwordActual: '' });
                }}
                className={`pr-10 ${errors.passwordActual ? 'border-red-500' : ''}`}
                placeholder="Ingresa tu contraseña actual"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, actual: !showPasswords.actual })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPasswords.actual ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.passwordActual && (
              <p className="text-red-500 text-sm mt-1">{errors.passwordActual}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <Label htmlFor="passwordNueva" className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-green-600" />
              Nueva Contraseña
            </Label>
            <div className="relative">
              <Input
                id="passwordNueva"
                type={showPasswords.nueva ? 'text' : 'password'}
                value={formData.passwordNueva}
                onChange={(e) => {
                  setFormData({ ...formData, passwordNueva: e.target.value });
                  setErrors({ ...errors, passwordNueva: '' });
                }}
                className={`pr-10 ${errors.passwordNueva ? 'border-red-500' : ''}`}
                placeholder="Ingresa tu nueva contraseña"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, nueva: !showPasswords.nueva })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPasswords.nueva ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.passwordNueva && (
              <p className="text-red-500 text-sm mt-1">{errors.passwordNueva}</p>
            )}
            
            {/* Password Strength Indicator */}
            {formData.passwordNueva && !errors.passwordNueva && (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${
                        passwordStrength.level === 1 ? 'bg-red-500 w-1/4' :
                        passwordStrength.level === 2 ? 'bg-yellow-500 w-2/4' :
                        passwordStrength.level === 3 ? 'bg-green-500 w-3/4' :
                        'bg-blue-500 w-full'
                      }`}
                    />
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded ${passwordStrength.color}`}>
                    {passwordStrength.text}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <Label htmlFor="passwordConfirmar" className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              Confirmar Nueva Contraseña
            </Label>
            <div className="relative">
              <Input
                id="passwordConfirmar"
                type={showPasswords.confirmar ? 'text' : 'password'}
                value={formData.passwordConfirmar}
                onChange={(e) => {
                  setFormData({ ...formData, passwordConfirmar: e.target.value });
                  setErrors({ ...errors, passwordConfirmar: '' });
                }}
                className={`pr-10 ${errors.passwordConfirmar ? 'border-red-500' : ''}`}
                placeholder="Confirma tu nueva contraseña"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirmar: !showPasswords.confirmar })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPasswords.confirmar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.passwordConfirmar && (
              <p className="text-red-500 text-sm mt-1">{errors.passwordConfirmar}</p>
            )}
            {formData.passwordConfirmar && formData.passwordNueva === formData.passwordConfirmar && !errors.passwordConfirmar && (
              <p className="text-green-600 text-sm mt-1 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                Las contraseñas coinciden
              </p>
            )}
          </div>

          {/* Security Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800 font-medium mb-2">💡 Consejos de seguridad:</p>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Usa al menos 8 caracteres</li>
              <li>• Combina mayúsculas, minúsculas, números y símbolos</li>
              <li>• No uses información personal obvia</li>
            </ul>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Actualizando...' : '🔒 Actualizar Contraseña'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CambiarPasswordModal;
